import type { Editor } from '@tiptap/react';
import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';

import {
  draftsHaveSameContent,
  getProjectsComposerStarterBundle,
  isProjectsComposerDraftEmpty,
  starterBundleIdentity,
  withoutProjectsComposerStarterBundle,
  withProjectsComposerStarterBundle,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerStarterBundle,
} from '../../data';
import type { ComposerPromptEditorHandle } from './Composer';

export interface UseComposerDraftArgs {
  draftValue: ProjectsComposerPromptDraft;
  managedStarterBundle?: ProjectsComposerStarterBundle | null;
  editorRef: RefObject<ComposerPromptEditorHandle | null>;
  editor: Editor | null;
  onDraftChange: (draft: ProjectsComposerPromptDraft) => void;
  onDraftSettled: (
    next: ProjectsComposerPromptDraft,
    previous: ProjectsComposerPromptDraft
  ) => void;
}

export interface ComposerDraft {
  draftRef: MutableRefObject<ProjectsComposerPromptDraft>;
  handleDraftChange: (next: ProjectsComposerPromptDraft) => void;
}

export const useComposerDraft = ({
  draftValue,
  managedStarterBundle,
  editorRef,
  editor,
  onDraftChange,
  onDraftSettled,
}: UseComposerDraftArgs): ComposerDraft => {
  const draftRef = useRef(draftValue);
  draftRef.current = draftValue;
  const appliedManagedBundleRef = useRef<{ editor: Editor; key: string } | null>(null);

  const handleDraftChange = (next: ProjectsComposerPromptDraft) => {
    if (
      !editor &&
      !isProjectsComposerDraftEmpty(draftValue) &&
      isProjectsComposerDraftEmpty(next)
    ) {
      return;
    }
    const previous = draftRef.current;
    if (draftsHaveSameContent(next, previous)) return;
    draftRef.current = next;
    onDraftChange(next);
    onDraftSettled(next, previous);
  };

  // The editor mounts a render after the composer (`immediatelyRender: false`),
  // so a draft restored on mount reaches `draftValue` with no editor to land in.
  // An editor that attaches empty under a non-empty draft takes the draft.
  const draftIsEmpty = isProjectsComposerDraftEmpty(draftValue);
  useEffect(() => {
    if (!editor || !editor.isEmpty || draftIsEmpty) return;
    editorRef.current?.replaceDraft(draftRef.current);
  }, [draftIsEmpty, editor, editorRef]);

  useEffect(() => {
    if (!editor || managedStarterBundle === undefined) return;
    const incoming = draftRef.current;
    const managedKey = starterBundleIdentity(managedStarterBundle);
    const currentKey = starterBundleIdentity(getProjectsComposerStarterBundle(incoming));
    if (currentKey === managedKey) {
      appliedManagedBundleRef.current = { editor, key: managedKey };
      return;
    }
    if (
      appliedManagedBundleRef.current?.editor === editor &&
      appliedManagedBundleRef.current.key === managedKey
    ) {
      return;
    }
    appliedManagedBundleRef.current = { editor, key: managedKey };
    const nextDraft = managedStarterBundle
      ? withProjectsComposerStarterBundle(incoming, managedStarterBundle)
      : withoutProjectsComposerStarterBundle(incoming);
    if (draftsHaveSameContent(nextDraft, incoming)) return;
    editorRef.current?.replaceDraft(nextDraft);
  }, [editor, editorRef, managedStarterBundle]);

  return { draftRef, handleDraftChange };
};
